import React, { useMemo } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { useGetReportFilterOptionsQuery } from "../../Services/ReportsService";

/**
 * The Product and Denomination dropdowns, shared by every report filter
 * panel.
 *
 * Denomination is dependent on Product: it offers only the denominations
 * that belong to the product chosen, and it is disabled until one is. That
 * dependency is the whole point of the pair, so it is enforced here rather
 * than left to each panel to remember -- five panels each remembering it is
 * five chances to forget.
 *
 * Both lists come from ONE cached request (GET /reports/filter-options),
 * with the denominations nested inside their product. So changing the
 * product repopulates the denominations with no network call and no
 * in-flight state: there is no moment where the second dropdown is empty for
 * a reason the person cannot see.
 *
 * What this replaces on each panel: a free-text "Search Product Name" box
 * that queried the product list as you typed. A name that was nearly right
 * returned nothing and explained nothing, and there was no way to filter by
 * denomination at all.
 */

export type ProductOption = {
  productId: string;
  productName: string;
  productType: string;
  denominations: { sku: string; denomination: number | null; label: string }[];
};

const ProductDenominationFilter: React.FC<{
  productId?: string;
  denominationSku?: string;
  /**
   * Called for each field. Clearing the product also clears the
   * denomination, because a denomination with no product behind it filters
   * on a SKU the person can no longer see.
   */
  onChange: (field: "productId" | "productDenominationsku", value: string) => void;
}> = ({ productId, denominationSku, onChange }) => {
  const { data, isLoading } = useGetReportFilterOptionsQuery();

  const products: ProductOption[] = useMemo(() => data?.data || [], [data]);

  const selectedProduct = useMemo(
    () => products.find((p) => p.productId === productId) || null,
    [products, productId],
  );

  const denominations = selectedProduct?.denominations || [];

  const selectedDenomination = useMemo(
    () => denominations.find((d) => d.sku === denominationSku) || null,
    [denominations, denominationSku],
  );

  return (
    <>
      <Autocomplete
        options={products}
        value={selectedProduct}
        loading={isLoading}
        // Two products can share a name across suppliers, so the id decides
        // which option is selected, never the label.
        isOptionEqualToValue={(o, v) => o.productId === v.productId}
        getOptionLabel={(o) => o.productName || ""}
        onChange={(_e, v) => {
          onChange("productId", v?.productId || "");
          // The denomination belonged to the previous product. Left behind,
          // it would keep filtering on a SKU no longer offered anywhere on
          // the panel -- an invisible filter returning nothing.
          onChange("productDenominationsku", "");
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Product"
            placeholder="All products"
            variant="outlined"
            size="small"
          />
        )}
        noOptionsText={isLoading ? "Loading products…" : "No products"}
      />

      <Autocomplete
        options={denominations}
        value={selectedDenomination}
        // Nothing to choose from until a product is picked. Disabled rather
        // than shown empty, so the reason is on the control itself.
        disabled={!selectedProduct}
        isOptionEqualToValue={(o, v) => o.sku === v.sku}
        getOptionLabel={(o) => o.label || o.sku || ""}
        onChange={(_e, v) => onChange("productDenominationsku", v?.sku || "")}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Denomination"
            placeholder={
              selectedProduct ? "All denominations" : "Choose a product first"
            }
            variant="outlined"
            size="small"
          />
        )}
        noOptionsText={
          selectedProduct
            ? "This product has no denominations"
            : "Choose a product first"
        }
      />
    </>
  );
};

/**
 * The names behind the two ids, for the applied-filter chips.
 *
 * A chip reading "Product: 66ab3f..." is no use, and leaving the two off
 * the chip row is worse: the filter is applied, invisible on the page, and
 * cannot be cleared from there -- a filter you cannot see is the reason
 * half the things fixed on these screens looked like bugs.
 *
 * Reads the SAME cached query the dropdowns use, so this costs no request.
 */
export const useProductDenominationLabels = (
  productId?: string,
  denominationSku?: string,
) => {
  const { data } = useGetReportFilterOptionsQuery();

  return useMemo(() => {
    if (!productId) return { productLabel: "", denominationLabel: "" };
    const product = (data?.data || []).find((p) => p.productId === productId);
    // Falls back to the id rather than showing an empty chip: better a chip
    // that looks odd than a filter with no sign it is on.
    const productLabel = product?.productName || productId;
    const denomination = denominationSku
      ? product?.denominations.find((d) => d.sku === denominationSku)
      : undefined;
    return {
      productLabel,
      denominationLabel: denominationSku
        ? denomination?.label || denominationSku
        : "",
    };
  }, [data, productId, denominationSku]);
};

export default ProductDenominationFilter;
